import { useSyncExternalStore } from 'react';
import type { ScaleSourceFeature } from './scaleFeatureSource';

export interface VectorScaleEdit {
  readonly layerId: string;
  readonly featureId: string | number;
  readonly feature: ScaleSourceFeature;
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly fillColor: string;
  readonly fillOpacity: number;
}

interface HistoryEntry {
  readonly current: VectorScaleEdit;
  readonly previous: readonly VectorScaleEdit[];
}

const edits = new Map<string, HistoryEntry>();
const listeners = new Set<() => void>();
let version = 0;

function keyOf(layerId: string, featureId: string | number): string {
  return `${layerId}\u0000${String(featureId)}`;
}

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

export function commitVectorScaleEdit(edit: VectorScaleEdit): void {
  const key = keyOf(edit.layerId, edit.featureId);
  const existing = edits.get(key);
  edits.set(key, {
    current: edit,
    previous: existing
      ? [...existing.previous, existing.current]
      : [],
  });
  notify();
}

/** Returns the replacement edit, or null when Undo restored the source shape. */
export function undoVectorScaleEdit(
  layerId: string,
  featureId: string | number,
): VectorScaleEdit | null {
  const key = keyOf(layerId, featureId);
  const existing = edits.get(key);
  if (!existing) return null;
  const prior = existing.previous.at(-1);
  if (!prior) {
    edits.delete(key);
    notify();
    return null;
  }
  edits.set(key, {
    current: prior,
    previous: existing.previous.slice(0, -1),
  });
  notify();
  return prior;
}

export function getVectorScaleEdit(
  layerId: string,
  featureId: string | number | null | undefined,
): VectorScaleEdit | null {
  if (featureId == null) return null;
  return edits.get(keyOf(layerId, featureId))?.current ?? null;
}

export function getAllVectorScaleEdits(): readonly VectorScaleEdit[] {
  return [...edits.values()].map((entry) => entry.current);
}

export function hasVectorScaleEdit(
  layerId: string,
  featureId: string | number | null | undefined,
): boolean {
  return featureId != null && edits.has(keyOf(layerId, featureId));
}

export function useVectorScaleSessionVersion(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => version,
    () => 0,
  );
}

export function clearVectorScaleSessionForTests(): void {
  edits.clear();
  notify();
}
